import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Badge,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  Button,
  IconButton,
  Paper,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { 
  Headset as HeadsetIcon,
  Chat as ChatIcon,
  History as HistoryIcon,
  ArrowBack as ArrowBackIcon,
  Notifications as NotificationsIcon,
  ConfirmationNumber as TicketIcon,
  Dashboard as DashboardIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  BusinessCenter as BusinessCenterIcon,
  DragHandle as DragHandleIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// Import components
import Sidebar from './components/Sidebar';
import LiveChatTab from './components/LiveChatTab';
import TicketsTab from './components/TicketsTab';
import HistoryTab from './components/HistoryTab';
import DashboardTab from './components/DashboardTab';
import TopAppBar from './components/layout/TopAppBar';
import MainNavigation from './components/layout/MainNavigation';
import ConversationsList from './components/layout/ConversationsList';
import DetailsSidebar from './components/layout/DetailsSidebar';
import MobileNavigation from './components/layout/MobileNavigation';

// Import helpers
import { TICKET_CATEGORIES } from './utils/constants';
import { generateMockTickets, generateMockUsers } from './utils/mockData';
import './styles/animations.css';

const CustomerServicePage = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const chatContainerRef = useRef(null);
  
  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  
  // State management
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTicket, setActiveTicket] = useState(null);
  const [activeSection, setActiveSection] = useState('inbox');
  const [unreadCount, setUnreadCount] = useState({
    inbox: 3,
    mentions: 2,
    unassigned: 5
  });
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [mobileView, setMobileView] = useState('navigation'); // 'navigation', 'tickets', 'chat', 'details'
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState('');
  const [selectedTab, setSelectedTab] = useState(0); // Add state for the tab control
  const [liveChatHistory, setLiveChatHistory] = useState([]);
  const [liveChatMessage, setLiveChatMessage] = useState('');
  const [liveChatSending, setLiveChatSending] = useState(false);
  const [activeUser, setActiveUser] = useState(null);
  const [conversationsBySections, setConversationsBySections] = useState({
    inbox: [],
    mentions: [],
    unassigned: [],
    closed: []
  });
  
  // Enhanced state for sliding panel with direct pixel values instead of percentages
  const [panelOpen, setPanelOpen] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [panelWidth, setPanelWidth] = useState(window.innerWidth * 0.75); // Start with 75% in pixels
  const resizeHandleRef = useRef(null);
  
  // Toggle panel function
  const togglePanel = () => {
    setPanelOpen(!panelOpen);
  };
  
  // Handle iframe load
  const handleIframeLoad = () => {
    setIframeLoading(false);
  };
  
  // Create memoized handler functions to avoid recreating them on each render
  const handleResizeMove = useCallback((e) => {
    if (!isDragging) return;
    
    // Get the width from right edge of window to mouse position
    const windowWidth = window.innerWidth;
    const newWidth = windowWidth - e.clientX;
    
    // Enforce min and max width constraints
    const minWidth = Math.min(windowWidth * 0.2, 200); // 20% or 200px, whichever is smaller
    const maxWidth = windowWidth * 0.95; // 95% of window width
    
    const clampedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
    
    // Update panel width
    setPanelWidth(clampedWidth);
    
    // Optional: Log for debugging
    console.log('Dragging: ', {newWidth, clampedWidth});
  }, [isDragging]);

  const handleResizeEnd = useCallback(() => {
    setIsDragging(false);
    document.body.style.cursor = '';
    document.body.classList.remove('resize-dragging');
    
    console.log('Resize ended');
  }, []);
  
  // Handle resize start
  const handleResizeStart = useCallback((e) => {
    // Prevent default browser behavior
    e.preventDefault();
    if (e.stopPropagation) e.stopPropagation();
    
    setIsDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.classList.add('resize-dragging');
    
    console.log('Resize started');
  }, []);
  
  // Set up event listeners
  useEffect(() => {
    // Only add listeners when dragging
    if (isDragging) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.addEventListener('mouseleave', handleResizeEnd);
    }
    
    // Clean up
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.removeEventListener('mouseleave', handleResizeEnd);
      document.body.classList.remove('resize-dragging');
      document.body.style.cursor = '';
    };
  }, [isDragging, handleResizeMove, handleResizeEnd]);
  
  // Handle window resize
  useEffect(() => {
    const handleWindowResize = () => {
      // Adjust panel width proportionally when window size changes
      const windowWidth = window.innerWidth;
      const ratio = panelWidth / windowWidth;
      setPanelWidth(windowWidth * ratio);
    };
    
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [panelWidth]);
  
  // Generate mock data on mount
  useEffect(() => {
    const mockTickets = generateMockTickets(20);
    setTickets(mockTickets);
    
    const mockUsers = generateMockUsers(8);
    setUsers(mockUsers);
    
    // Set random online users
    const randomOnline = mockUsers
      .filter(() => Math.random() > 0.3)
      .map(user => user.id);
    setOnlineUsers(randomOnline);
    
    // Set first user as active for live chat demo
    setActiveUser(mockUsers[0]);
    
    // Generate some mock chat history
    const mockChat = [
      {
        role: 'agent',
        content: 'Hello! How can I help you today?',
        timestamp: new Date(2025, 4, 15, 10, 30).toISOString(),
      },
      {
        role: 'customer',
        content: 'I have a question about my recent order',
        timestamp: new Date(2025, 4, 15, 10, 31).toISOString(),
      },
      {
        role: 'agent',
        content: 'Of course, I can help with that. Can you please provide your order number so I can look up the details?',
        timestamp: new Date(2025, 4, 15, 10, 32).toISOString(),
      },
      {
        role: 'customer',
        content: 'It\'s order #45678',
        timestamp: new Date(2025, 4, 15, 10, 33).toISOString(),
      }
    ];
    setLiveChatHistory(mockChat);
    
    generateSectionConversations(mockUsers);
    
    setLoading(false);
  }, []);

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [activeTicket]);
  
  // Handle section change
  const handleSectionChange = (section) => {
    setActiveSection(section);
    // Reset unread count for this section
    setUnreadCount(prev => ({ ...prev, [section]: 0 }));
    
    // On mobile, switch to tickets view when changing sections
    if (isMobile) {
      setMobileView('tickets');
    }
  };
  
  // Handle ticket selection
  const handleSelectTicket = (ticketId) => {
    setActiveTicket(ticketId);
    
    // Switch to tickets tab when selecting a ticket
    setSelectedTab(1);
    
    // Reset expanded ticket when selecting a new ticket
    setExpandedTicket(null);
    
    // On mobile, switch to chat view when selecting a ticket
    if (isMobile) {
      setMobileView('chat');
    }
  };
  
  // Add handler for expanding ticket details
  const handleExpandTicket = (ticketId) => {
    setExpandedTicket(ticketId === expandedTicket ? null : ticketId);
  };
  
  // Handle mobile view navigation
  const handleMobileNavigation = (view) => {
    setMobileView(view);
  };
  
  // Handle send message in active ticket
  const handleSendMessage = () => {
    if (!message.trim() || !activeTicket) return;
    
    const newMessage = {
      id: `msg-${Date.now()}`,
      ticketId: activeTicket,
      senderId: user?.id || 'current-user',
      senderName: user?.name || 'You',
      content: message,
      timestamp: new Date().toISOString(),
      read: true
    };
    
    // Add message to ticket
    setTickets(prev => prev.map(ticket => {
      if (ticket.id === activeTicket) {
        return {
          ...ticket,
          messages: [...ticket.messages, newMessage],
          lastActivity: new Date().toISOString()
        };
      }
      return ticket;
    }));
    
    // Clear message input
    setMessage('');
    
    // Simulate response after delay
    setTimeout(() => {
      const responseMessage = {
        id: `msg-${Date.now() + 1}`,
        ticketId: activeTicket,
        senderId: 'system',
        senderName: 'Support Bot',
        content: `Thank you for your message. Our team will review your inquiry and respond shortly. Is there anything else I can help with?`,
        timestamp: new Date().toISOString(),
        read: true
      };
      
      setTickets(prev => prev.map(ticket => {
        if (ticket.id === activeTicket) {
          return {
            ...ticket,
            messages: [...ticket.messages, responseMessage],
            lastActivity: new Date().toISOString()
          };
        }
        return ticket;
      }));
    }, 1500);
  };
  
  // Handle send message in live chat
  const handleSendLiveMessage = () => {
    if (!liveChatMessage.trim()) return;
    
    // Add agent message to chat
    const agentMessage = liveChatMessage;
    const newAgentMsg = { 
      role: 'agent', 
      content: agentMessage,
      timestamp: new Date().toISOString()
    };
    
    // Update the live chat history
    setLiveChatHistory(prev => [...prev, newAgentMsg]);
    
    // Also update the conversations by section
    setConversationsBySections(prev => ({
      ...prev,
      [activeSection]: [...(prev[activeSection] || []), newAgentMsg]
    }));
    
    setLiveChatMessage('');
    setLiveChatSending(true);
    
    // Simulate customer response after a delay
    setTimeout(() => {
      const customerResponses = [
        "Thank you for the information.",
        "That's helpful, I appreciate it.",
        "I understand now. What would you recommend I do next?",
        "Could you explain that in more detail?",
        "I'll try that solution and let you know how it goes."
      ];
      
      const randomResponse = customerResponses[Math.floor(Math.random() * customerResponses.length)];
      
      const customerMsg = { 
        role: 'customer', 
        content: randomResponse,
        timestamp: new Date().toISOString()
      };
      
      // Update the live chat history
      setLiveChatHistory(prev => [...prev, customerMsg]);
      
      // Also update the conversations by section
      setConversationsBySections(prev => ({
        ...prev,
        [activeSection]: [...(prev[activeSection] || []), customerMsg]
      }));
      
      setLiveChatSending(false);
    }, 1500);
  };

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };
  
  // Filter tickets based on active section and search query
  const filteredTickets = tickets.filter(ticket => {
    // First filter by section
    if (activeSection === 'inbox') {
      // All tickets that aren't archived
      if (ticket.status === 'archived') return false;
    } else if (activeSection === 'mentions') {
      // Tickets where user is mentioned
      const isMentioned = ticket.messages.some(
        msg => msg.content.includes(`@${user?.name || 'user'}`)
      );
      if (!isMentioned) return false;
    } else if (activeSection === 'unassigned') {
      // Tickets without assignees
      if (ticket.assignedTo) return false;
    } else if (activeSection === 'closed') {
      // Only resolved/closed tickets
      if (ticket.status !== 'closed') return false;
    }
    
    // Then filter by search query if present
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        ticket.subject.toLowerCase().includes(query) ||
        ticket.customer.name.toLowerCase().includes(query) ||
        ticket.id.toLowerCase().includes(query) ||
        ticket.messages.some(msg => msg.content.toLowerCase().includes(query))
      );
    }
    
    return true;
  });

  // Get active ticket data
  const activeTicketData = tickets.find(t => t.id === activeTicket);
  
  // Generate conversations for the different inbox sections
  const generateSectionConversations = (mockUsers) => {
    // For inbox section
    const inboxChat = [
      {
        role: 'agent',
        content: 'Hello! How can I help you today?',
        timestamp: new Date(2025, 4, 15, 10, 30).toISOString(),
      },
      {
        role: 'customer',
        content: 'I have a question about my recent order',
        timestamp: new Date(2025, 4, 15, 10, 31).toISOString(),
      },
      {
        role: 'agent',
        content: 'Of course, I can help with that. Can you please provide your order number so I can look up the details?',
        timestamp: new Date(2025, 4, 15, 10, 32).toISOString(),
      },
      {
        role: 'customer',
        content: 'It\'s order #45678',
        timestamp: new Date(2025, 4, 15, 10, 33).toISOString(),
      }
    ];
    
    // For mentions section
    const mentionsChat = [
      {
        role: 'customer',
        content: 'I need to speak with @support about my refund',
        timestamp: new Date(2025, 4, 14, 14, 10).toISOString(),
      },
      {
        role: 'agent',
        content: 'Hello, this is the support team. I understand you need help with a refund. Can you provide your order details?',
        timestamp: new Date(2025, 4, 14, 14, 15).toISOString(),
      },
    ];
    
    // For unassigned section
    const unassignedChat = [
      {
        role: 'customer',
        content: 'Is anyone available to help with technical issues?',
        timestamp: new Date(2025, 4, 15, 9, 45).toISOString(),
      }
    ];
    
    // For closed section
    const closedChat = [
      {
        role: 'customer',
        content: 'How do I reset my password?',
        timestamp: new Date(2025, 4, 10, 8, 30).toISOString(),
      },
      {
        role: 'agent',
        content: 'You can reset your password by clicking the "Forgot Password" link on the login page. Would you like me to send you a direct link?',
        timestamp: new Date(2025, 4, 10, 8, 35).toISOString(),
      },
      {
        role: 'customer',
        content: 'Yes please, that would be helpful',
        timestamp: new Date(2025, 4, 10, 8, 37).toISOString(),
      },
      {
        role: 'agent',
        content: 'I\'ve sent a password reset link to your email. Let me know if you need anything else!',
        timestamp: new Date(2025, 4, 10, 8, 40).toISOString(),
      },
      {
        role: 'customer',
        content: 'Thank you, that worked perfectly!',
        timestamp: new Date(2025, 4, 10, 8, 50).toISOString(),
      }
    ];
    
    // Update the conversations by section state
    setConversationsBySections({
      inbox: inboxChat,
      mentions: mentionsChat,
      unassigned: unassignedChat,
      closed: closedChat
    });
    
    // Set the initial chat history to the inbox conversations
    setLiveChatHistory(inboxChat);
  };
  
  // Update chat history when active section changes
  useEffect(() => {
    // Set the live chat history based on the active section
    setLiveChatHistory(conversationsBySections[activeSection] || []);
  }, [activeSection]);
  
  // Calculate percentage for styling
  const panelWidthPercent = `${(panelWidth / window.innerWidth) * 100}%`;
  
  return (
    <Box sx={{ 
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #121212 0%, #2c3e50 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background patterns and decorations */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z' fill='%23ffffff' fill-opacity='0.3' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          zIndex: 0
        }}
      />
      
      {/* Top App Bar - Enhanced with back to Dashboard link */}
      <AppBar 
        position="fixed"
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'rgba(30, 35, 45, 0.85)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}
      >
        <Toolbar>
          {/* Dashboard navigation links */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Tooltip title="Back to Dashboard">
              <IconButton
                component={Link}
                to="/"
                edge="start"
                color="inherit"
                aria-label="back to dashboard"
                sx={{ 
                  mr: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  }
                }}
              >
                <HomeIcon />
              </IconButton>
            </Tooltip>

            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}
            >
              <HeadsetIcon sx={{ mr: 1 }} /> Customer Support
            </Typography>
          </Box>

          {/* Use the existing TopAppBar component, but we need to pass a prop to indicate we've added our own title */}
          <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-end' }}>
            <TopAppBar 
              user={user}
              isMobile={isMobile}
              isTablet={isTablet}
              onSearch={handleSearch}
              hideTitle={true} // Add this prop to hide the title in the TopAppBar component
            />
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Main Content Area */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
          zIndex: 1,
          transition: isDragging ? 'none' : 'margin-right 0.3s ease-in-out',
          marginRight: panelOpen ? `${panelWidth}px` : '30px'
        }}
      >
        {/* Main Navigation - Hide on mobile */}
        {(!isMobile || mobileView === 'navigation') && (
          <MainNavigation 
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            unreadCount={unreadCount}
            onlineUsers={onlineUsers}
            users={users}
            theme={theme}
            isMobile={isMobile}
          />
        )}
        
        {/* Conversations/Tickets List - Hide on mobile if not in tickets view */}
        {(!isMobile || mobileView === 'tickets') && (
          <ConversationsList 
            tickets={filteredTickets}
            activeTicket={activeTicket}
            onSelectTicket={handleSelectTicket}
            searchQuery={searchQuery}
            onSearch={handleSearch}
            activeSection={activeSection}
            theme={theme}
            isMobile={isMobile}
            onBackToNavigation={() => setMobileView('navigation')}
          />
        )}
        
        {/* Main content area with tabs */}
        {(!isMobile || mobileView === 'chat') && (
          <Paper 
            sx={{ 
              flex: 1,
              borderRadius: 0,
              display: 'flex',
              flexDirection: 'column',
              background: 'rgba(26, 32, 44, 0.7)',
              backdropFilter: 'blur(10px)',
              overflow: 'hidden'
            }}
          >
            <AppBar position="static" sx={{ backgroundColor: 'rgba(30, 35, 45, 0.7)', boxShadow: 'none' }}>
              <Tabs 
                value={selectedTab} 
                onChange={handleTabChange} 
                variant="fullWidth" 
                sx={{
                  '& .MuiTab-root': {
                    color: 'rgba(255,255,255,0.7)',
                    '&.Mui-selected': {
                      color: 'white'
                    }
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: theme.palette.primary.main
                  }
                }}
              >
                <Tab 
                  label="Dashboard" 
                  icon={<DashboardIcon />} 
                  iconPosition="start"
                />
                <Tab 
                  label="Tickets" 
                  icon={<TicketIcon />} 
                  iconPosition="start"
                  sx={{ 
                    '& .MuiBadge-badge': {
                      right: -3,
                      top: 13,
                      border: `2px solid ${theme.palette.background.paper}`,
                      padding: '0 4px',
                    }
                  }}
                />
                <Tab 
                  label="Live Chat" 
                  icon={<ChatIcon />} 
                  iconPosition="start"
                />
                <Tab 
                  label="History" 
                  icon={<HistoryIcon />} 
                  iconPosition="start"
                />
              </Tabs>
            </AppBar>
            
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              {/* Dashboard Tab */}
              <Box 
                hidden={selectedTab !== 0} 
                sx={{ 
                  height: '100%', 
                  overflow: 'auto',
                  px: { xs: 1, sm: 2, md: 3 },
                  py: 2
                }}
              >
                {selectedTab === 0 && (
                  <DashboardTab 
                    tickets={tickets} 
                    onSelectTicket={handleSelectTicket} 
                  />
                )}
              </Box>
              
              {/* Tickets Tab */}
              <Box hidden={selectedTab !== 1} sx={{ height: '100%' }}>
                <TicketsTab 
                  visible={selectedTab === 1}
                  activeTicket={activeTicketData}
                  tickets={tickets}
                  setTickets={setTickets}
                  onSelectTicket={handleSelectTicket}
                  theme={theme}
                  isMobile={isMobile}
                  onBackToTickets={() => setMobileView('tickets')}
                  onViewDetails={() => setMobileView('details')}
                  expandedTicket={expandedTicket}
                  setExpandedTicket={handleExpandTicket} // Pass the handler function
                />
              </Box>
              
              {/* Live Chat Tab */}
              <Box hidden={selectedTab !== 2} sx={{ height: '100%' }}>
                <LiveChatTab 
                  visible={selectedTab === 2} 
                  chatHistory={activeTicketData?.messages || []}
                  message={message}
                  setMessage={setMessage}
                  sending={liveChatSending} // Fixed: Changed 'sending' to 'liveChatSending'
                  handleSendMessage={handleSendMessage}
                  chatContainerRef={chatContainerRef}
                  theme={theme}
                  activeUser={activeUser}
                  onlineUsers={onlineUsers}
                  activeSection={activeSection}
                  activeTicket={activeTicketData} // Pass the activeTicket data
                  onBackToTickets={() => setMobileView('tickets')} // Pass the back handler
                  isMobile={isMobile} // Pass the isMobile flag
                />
              </Box>
              
              {/* History Tab */}
              <Box hidden={selectedTab !== 3} sx={{ height: '100%', overflow: 'auto' }}>
                <HistoryTab 
                  visible={selectedTab === 3}
                  tickets={tickets}
                  users={users}
                  theme={theme}
                />
              </Box>
            </Box>
          </Paper>
        )}
        
        {/* Details Sidebar - Hide on mobile and tablet if not in details view */}
        {(isDesktop || (isMobile && mobileView === 'details')) && (
          <DetailsSidebar
            ticket={activeTicketData}
            users={users}
            onlineUsers={onlineUsers}
            theme={theme}
            isMobile={isMobile}
            onBackToChat={() => setMobileView('chat')}
          />
        )}
      </Box>
      
      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <MobileNavigation
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          activeView={mobileView}
          onViewChange={handleMobileNavigation}
          unreadCount={unreadCount}
          theme={theme}
          selectedTab={selectedTab}
          onTabChange={handleTabChange}
        />
      )}

      {/* Sliding Panel Toggle Bar (always visible) */}
      <Box
        sx={{
          position: 'fixed',
          right: panelOpen ? `${panelWidth}px` : 0,
          top: 0,
          bottom: 0,
          width: '30px',
          backgroundColor: 'rgba(30, 35, 45, 0.85)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
          cursor: 'pointer',
          zIndex: theme.zIndex.drawer + 2,
          backdropFilter: 'blur(10px)',
          boxShadow: '-2px 0 10px rgba(0, 0, 0, 0.2)',
          transition: isDragging ? 'none' : 'right 0.3s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(40, 45, 55, 0.95)',
          }
        }}
        onClick={togglePanel}
      >
        <Box 
          sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <IconButton sx={{ color: 'white', p: 0, my: 1 }}>
            {panelOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
          
          {/* Vertical text */}
          <Typography
            sx={{
              color: 'white',
              transform: 'rotate(90deg)',
              whiteSpace: 'nowrap',
              marginTop: 10,
              fontWeight: 'medium',
              letterSpacing: '1px',
              fontSize: '0.75rem'
            }}
          >
            BETFI BACKOFFICE
          </Typography>

          <Box sx={{ mt: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <BusinessCenterIcon sx={{ color: 'white', fontSize: 20, mb: 1 }} />
          </Box>
        </Box>
      </Box>

      {/* Improved Resize Handle with better visibility and interactivity */}
      {panelOpen && (
        <Box
          ref={resizeHandleRef}
          sx={{
            position: 'fixed',
            right: `${panelWidth}px`,
            top: 0,
            bottom: 0,
            width: '20px', // Much wider for easier grabbing
            backgroundColor: isDragging ? 'rgba(76, 133, 240, 0.3)' : 'rgba(76, 133, 240, 0.1)',
            cursor: 'col-resize',
            zIndex: theme.zIndex.drawer + 3,
            transition: 'none',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            '&:hover': {
              backgroundColor: 'rgba(76, 133, 240, 0.3)', 
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              width: '6px',
              backgroundColor: isDragging ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.5)',
              borderRadius: '3px',
              opacity: isDragging ? 1 : 0.5,
            },
            // Better touch support
            touchAction: 'none'
          }}
          onMouseDown={handleResizeStart}
          onClick={(e) => e.stopPropagation()} // Prevent toggle panel when clicking resize handle
        />
      )}

      {/* Sliding Panel with iframe */}
      <Box
        sx={{
          position: 'fixed',
          right: panelOpen ? 0 : `-${panelWidth}px`,
          top: 0,
          bottom: 0,
          width: `${panelWidth}px`,
          backgroundColor: '#fff',
          boxShadow: '-5px 0 25px rgba(0, 0, 0, 0.3)',
          zIndex: theme.zIndex.drawer + 1,
          transition: isDragging ? 'none' : 'right 0.3s ease-in-out',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Panel Header */}
        <Box
          sx={{
            height: '64px',
            backgroundColor: 'rgba(30, 35, 45, 0.95)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            borderLeft: isDragging ? `4px solid ${theme.palette.primary.main}` : 'none',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessCenterIcon sx={{ mr: 1 }} />
            BetFi BackOffice {isDragging && `(${Math.round(panelWidth)}px)`}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)', 
                mr: 1,
                '&:hover': { color: 'white' } 
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleResizeStart(e);
              }}
              aria-label="resize panel"
            >
              <DragHandleIcon />
            </IconButton>
            <IconButton sx={{ color: 'white' }} onClick={togglePanel} aria-label="close panel">
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
        
        {/* Loading indicator */}
        {iframeLoading && (
          <Box 
            sx={{
              position: 'absolute',
              top: '64px',
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              backgroundColor: 'rgba(30, 35, 45, 0.9)',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ mt: 2, color: 'white' }}>
                Loading BetFi BackOffice...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Iframe container */}
        <Box sx={{ flexGrow: 1, height: 'calc(100% - 64px)' }}>
          <iframe 
            src="https://dev-management.betfi365.com/" 
            title="BetFi BackOffice"
            style={{ 
              width: '100%', 
              height: '100%', 
              border: 'none',
              backgroundColor: '#fff',
              pointerEvents: isDragging ? 'none' : 'auto'
            }}
            onLoad={handleIframeLoad}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          />
        </Box>
      </Box>

      {/* Enhanced global styles for drag operations */}
      <style jsx global>{`
        body.resize-dragging {
          cursor: col-resize !important;
          user-select: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          overflow: hidden !important;
        }
        
        body.resize-dragging * {
          cursor: col-resize !important;
          user-select: none !important;
        }
        
        body.resize-dragging iframe {
          pointer-events: none !important;
        }

        .resize-handle-active {
          background-color: rgba(76, 133, 240, 0.3) !important;
        }
        
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.8; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </Box>
  );
};

export default CustomerServicePage;
